import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot as SlotPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';
import { Spinner } from './spinner';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md cursor-pointer text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export type ButtonElement = React.ComponentRef<'button'>;
export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };
function Button({
  className,
  variant,
  size,
  loading = false,
  asChild = false,
  children,
  ...props
}: ButtonProps): React.JSX.Element {
  const Comp = asChild ? SlotPrimitive.Slot : 'button';

  const buttonProps = asChild
    ? {
        ...props
      }
    : {
        className: cn(
          buttonVariants({ variant, size, className }),
          loading && 'relative'
        ),
        'data-slot': 'button',
        ...props
      };

  const content = loading ? (
    <>
      <span className={cn({ 'opacity-0': loading })}>{children}</span>
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <Spinner />
      </span>
    </>
  ) : (
    <>{children}</>
  );

  if (asChild) {
    return (
      <Comp {...buttonProps}>
        <span
          className={cn(
            buttonVariants({ variant, size, className }),
            loading && 'relative'
          )}
        >
          {content}
        </span>
      </Comp>
    );
  }

  return <Comp {...buttonProps}>{content}</Comp>;
}

export { Button, buttonVariants };
